//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useCallback, useMemo, useState, type ReactNode } from 'react';

import SettingsModal from '@/components/Settings/SettingsModal';
import {
  DEFAULT_LEAF_KEY, type SettingsScope
} from '@/components/Settings/settingsSections';
import { SettingsContext } from '@/contexts/SettingsContext';

export const SettingsProvider = ({ children }: { children: ReactNode }) => {
  const [open, setOpen] = useState(false);
  const [selectedLeafKey, setSelectedLeafKey] = useState(DEFAULT_LEAF_KEY);
  const [scope, setScope] = useState<SettingsScope | undefined>(undefined);

  const openSettings = useCallback(
    (leafKey?: string, nextScope?: SettingsScope) => {
      setSelectedLeafKey(leafKey ?? DEFAULT_LEAF_KEY);
      setScope(nextScope);
      setOpen(true);
    }, []);

  const selectLeaf = useCallback(
    (leafKey: string) => setSelectedLeafKey(leafKey), []);

  const closeSettings = useCallback(() => setOpen(false), []);

  const value = useMemo(
    () => ({
      open, selectedLeafKey, scope, openSettings, selectLeaf, closeSettings
    }),
    [open, selectedLeafKey, scope, openSettings, selectLeaf, closeSettings]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
      <SettingsModal />
    </SettingsContext.Provider>
  );
};
